import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts';
import { getClientConfig } from './config';

/**
 * OIDC Authorization Code + PKCE sign-in for Okta/Entra. One generic OIDC client works for
 * both IdPs; the difference is purely the server-provided authority/clientId/scope. Identity
 * mode never touches this module (it uses the cookie login form instead).
 *
 * The access token is mirrored into `sessionStorage['access_token']` on every user-loaded /
 * token-renew event so `lib/api.ts` always attaches a current bearer token to API calls.
 */
let _managerPromise: Promise<UserManager> | null = null;

function syncAccessToken(user: User | null) {
  if (user?.access_token) {
    sessionStorage.setItem('access_token', user.access_token);
  } else {
    sessionStorage.removeItem('access_token');
  }
}

async function getUserManager(): Promise<UserManager> {
  if (_managerPromise) return _managerPromise;

  _managerPromise = (async () => {
    const cfg = await getClientConfig();
    if (!cfg.oidcAuthority || !cfg.oidcClientId) {
      throw new Error('OIDC is not configured for this environment.');
    }

    const manager = new UserManager({
      authority: cfg.oidcAuthority,
      client_id: cfg.oidcClientId,
      redirect_uri: `${window.location.origin}/auth/callback`,
      post_logout_redirect_uri: window.location.origin,
      response_type: 'code',
      scope: cfg.oidcScope ?? 'openid profile email',
      automaticSilentRenew: true,
      userStore: new WebStorageStateStore({ store: window.sessionStorage })
    });

    // Keep the bearer token used by api.ts fresh across silent renewals and re-loads.
    manager.events.addUserLoaded((user) => syncAccessToken(user));
    manager.events.addAccessTokenExpiring(() => {
      void manager.signinSilent().then(syncAccessToken).catch(() => {/* renewal handled by lib gate */});
    });
    manager.events.addUserUnloaded(() => syncAccessToken(null));

    return manager;
  })();

  return _managerPromise;
}

/** Begin an Authorization Code + PKCE redirect to the IdP. */
export async function loginRedirect(): Promise<void> {
  const manager = await getUserManager();
  await manager.signinRedirect();
}

/** Complete the redirect back from the IdP. Writes the access token to sessionStorage on success. */
export async function handleCallback(): Promise<User> {
  const manager = await getUserManager();
  const user = await manager.signinRedirectCallback();
  syncAccessToken(user);
  return user;
}

/** Clear the local token and redirect to the IdP's logout endpoint. */
export async function logoutRedirect(): Promise<void> {
  const manager = await getUserManager();
  sessionStorage.removeItem('access_token');
  await manager.signoutRedirect();
}
